import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  ArrowLeft, History, ArrowUpRight, ArrowDownLeft, RefreshCw,
  ExternalLink, Filter, Coins, CreditCard, Repeat
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";
const UNISWAP_POOL = "0x8F01D20feaA323049976f35865F43D3C457F19CF";

const MyTransactions = () => {
  const { token } = useAuth();
  const { account } = useWeb3();
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (account) {
      fetchTransactions();
    }
  }, [account]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Fetch from our backend
      const [transfersRes, swapsRes] = await Promise.all([
        fetch(`${API}/user/transfers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/user/swaps?wallet=${account}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      let allTx = [];

      if (transfersRes.ok) {
        const data = await transfersRes.json();
        allTx = [...allTx, ...(data.transfers || []).map(t => ({
          ...t,
          type: 'transfer',
          direction: 'out'
        }))];
      }

      if (swapsRes.ok) {
        const data = await swapsRes.json();
        allTx = [...allTx, ...(data.swaps || []).map(s => ({
          ...s,
          type: 'swap'
        }))];
      }

      // Sort by date
      allTx.sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));
      
      setTransactions(allTx);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTransactions = () => {
    if (filter === 'all') return transactions;
    return transactions.filter(tx => tx.type === filter);
  };

  const getTransactionIcon = (tx) => {
    switch (tx.type) {
      case 'swap':
        return <Repeat className="w-5 h-5 text-pink-400" />;
      case 'transfer':
        return tx.direction === 'in' 
          ? <ArrowDownLeft className="w-5 h-5 text-green-400" />
          : <ArrowUpRight className="w-5 h-5 text-red-400" />;
      case 'deposit':
        return <ArrowDownLeft className="w-5 h-5 text-blue-400" />;
      case 'borrow':
        return <CreditCard className="w-5 h-5 text-purple-400" />;
      default:
        return <Coins className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTransactionLabel = (tx) => {
    switch (tx.type) {
      case 'swap':
        return 'Uniswap Swap';
      case 'transfer':
        return tx.direction === 'in' ? 'Received BETH' : 'Sent BETH';
      case 'deposit':
        return 'Collateral Deposit';
      case 'borrow':
        return 'Credit Draw';
      default:
        return 'Transaction';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="my-transactions">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <History className="w-5 h-5 text-[#b8860b]" />
                My Transactions
              </h1>
              <p className="text-xs text-gray-500">View your BETH transaction history</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchTransactions} className="border-[#1a2d4a]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
          >
            All
          </Button>
          <Button
            variant={filter === 'swap' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('swap')}
            className={filter === 'swap' ? 'bg-pink-600' : 'border-[#1a2d4a]'}
          >
            <Repeat className="w-4 h-4 mr-1" />
            Swaps
          </Button>
          <Button
            variant={filter === 'transfer' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('transfer')}
            className={filter === 'transfer' ? 'bg-blue-600' : 'border-[#1a2d4a]'}
          >
            <ArrowUpRight className="w-4 h-4 mr-1" />
            Transfers
          </Button>
        </div>

        {/* Quick Links */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">View on external explorers:</p>
              <div className="flex gap-2">
                {account && (
                  <a 
                    href={`https://etherscan.io/token/${BETH_TOKEN}?a=${account}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="border-[#1a2d4a]">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Etherscan
                    </Button>
                  </a>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-[#b8860b]/50 text-[#b8860b]"
                  onClick={() => navigate('/subscribe')}
                >
                  Get BETH Credits
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction List */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white">Transaction History</CardTitle>
            <CardDescription>Your BETH transfers and platform activity</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#b8860b] mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading transactions...</p>
              </div>
            ) : getFilteredTransactions().length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No transactions found</p>
                <p className="text-xs text-gray-500 mt-1">
                  Your BETH transactions will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {getFilteredTransactions().map((tx, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-4 bg-[#1a2d4a]/50 rounded-lg hover:bg-[#1a2d4a] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#1a2d4a] flex items-center justify-center">
                        {getTransactionIcon(tx)}
                      </div>
                      <div>
                        <p className="text-white font-medium">{getTransactionLabel(tx)}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(tx.created_at || tx.timestamp).toLocaleString()}
                        </p>
                        {tx.to && (
                          <p className="text-xs text-gray-500 font-mono">
                            To: {tx.to.slice(0, 8)}...{tx.to.slice(-6)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        tx.direction === 'in' || tx.type === 'swap' ? 'text-green-400' : 'text-white'
                      }`}>
                        {tx.direction === 'out' ? '-' : '+'}{tx.amount?.toLocaleString()} BETH
                      </p>
                      {tx.tx_hash && (
                        <a 
                          href={`https://etherscan.io/tx/${tx.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#b8860b] hover:underline flex items-center gap-1 justify-end"
                        >
                          View TX <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Filter className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Can&apos;t find a transaction?</p>
                <p className="text-sm text-gray-400">
                  Transactions may take a few minutes to appear. For the most accurate history, 
                  check <a href={`https://etherscan.io/address/${account}`} target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">Etherscan</a> directly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MyTransactions;
